import { createId } from './id';
import type { ModelDocument, ModelEntity, Opening, Vec3Tuple } from './types';

const now = () => new Date().toISOString();

export function makeEntity(
  kind: ModelEntity['kind'],
  name: string,
  position: Vec3Tuple,
  dimensions: ModelEntity['dimensions'],
  options: Partial<ModelEntity> = {},
): ModelEntity {
  return {
    id: createId(kind),
    name,
    kind,
    position,
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    dimensions,
    color: kind === 'slab' ? '#9eaaa4' : kind === 'wall' ? '#d8dedb' : '#d2b48c',
    opacity: 1,
    visible: true,
    locked: false,
    layerId: 'layer_building',
    metadata: {},
    ...options,
  };
}

export function makeOpening(
  kind: Opening['kind'],
  center: number,
  defaults: Pick<Opening, 'width' | 'height' | 'sill' | 'color'>,
): Opening {
  return {
    id: createId(kind),
    kind,
    center,
    ...defaults,
  };
}

export function createBlankDocument(name = 'Untitled model'): ModelDocument {
  const timestamp = now();
  return {
    format: 'constructcanvas',
    version: 1,
    id: createId('project'),
    name,
    createdAt: timestamp,
    updatedAt: timestamp,
    revision: 0,
    entities: [],
    layers: [
      { id: 'layer_building', name: 'Building', color: '#62d995', visible: true, locked: false },
      { id: 'layer_site', name: 'Site', color: '#78a7ff', visible: true, locked: false },
      { id: 'layer_furniture', name: 'Furniture', color: '#f5b76f', visible: true, locked: false },
    ],
    scenes: [],
    settings: {
      units: 'metric',
      gridSize: 0.25,
      snapEnabled: true,
      shadows: true,
      showGrid: true,
      showAxes: true,
      background: '#dbe8e3',
      wallHeight: 2.7,
      wallThickness: 0.18,
      slabThickness: 0.2,
      doorWidth: 0.9,
      doorHeight: 2.1,
      windowWidth: 1.2,
      windowHeight: 1.2,
      windowSill: 0.9,
    },
  };
}

function wall(
  name: string,
  position: Vec3Tuple,
  length: number,
  thickness: number,
  height: number,
  rotationY = 0,
  openings: Opening[] = [],
): ModelEntity {
  return makeEntity(
    'wall',
    name,
    position,
    { width: length, height, depth: thickness },
    {
      rotation: [0, rotationY, 0],
      color: '#dce2df',
      openings,
      metadata: { assembly: 'Timber stud wall', fireRatingMinutes: 60 },
    },
  );
}

export function createDemoDocument(): ModelDocument {
  const doc = createBlankDocument('Courtyard studio');
  const width = 8;
  const depth = 6;
  const h = 2.7;
  const t = 0.18;

  doc.entities = [
    makeEntity('slab', 'Ground slab', [0, 0.1, 0], { width, height: 0.2, depth }, {
      color: '#9ca8a2',
      metadata: { material: 'Concrete', strength: '32 MPa' },
    }),
    wall('North wall', [0, h / 2, -depth / 2 + t / 2], width, t, h),
    wall('South wall', [0, h / 2, depth / 2 - t / 2], width, t, h, 0, [
      makeOpening('door', -2.3, { width: 0.92, height: 2.1, sill: 0, color: '#8d6e52' }),
      makeOpening('window', 1.35, { width: 1.8, height: 1.15, sill: 0.9, color: '#76b5cf' }),
    ]),
    wall('West wall', [-width / 2 + t / 2, h / 2, 0], depth, t, h, Math.PI / 2, [
      makeOpening('window', 0, { width: 1.5, height: 1.15, sill: 0.9, color: '#76b5cf' }),
    ]),
    wall('East wall', [width / 2 - t / 2, h / 2, 0], depth, t, h, Math.PI / 2),
    makeEntity('box', 'Kitchen island', [1.1, 0.45, 0.5], { width: 2.2, height: 0.9, depth: 0.8 }, {
      color: '#53655d',
      layerId: 'layer_furniture',
      metadata: { category: 'Joinery' },
    }),
    makeEntity('column', 'Feature column', [-2.2, 1.35, -1.4], { width: 0.3, height: 2.7, depth: 0.3 }, {
      color: '#b66b4a',
      metadata: { material: 'Brick' },
    }),
  ];

  return doc;
}

export function cloneDocument(document: ModelDocument): ModelDocument {
  return structuredClone(document);
}

export function normaliseDocument(input: unknown): ModelDocument {
  if (!input || typeof input !== 'object') throw new Error('The file does not contain a ConstructCanvas model.');
  const value = input as Partial<ModelDocument>;
  if (value.format !== 'constructcanvas' || value.version !== 1 || !Array.isArray(value.entities)) {
    throw new Error('Unsupported or invalid .ccmodel file.');
  }

  const base = createBlankDocument(typeof value.name === 'string' ? value.name : 'Imported model');
  return {
    ...base,
    ...value,
    settings: { ...base.settings, ...(value.settings ?? {}) },
    layers: Array.isArray(value.layers) ? value.layers : base.layers,
    scenes: Array.isArray(value.scenes) ? value.scenes : [],
    entities: value.entities as ModelEntity[],
    updatedAt: now(),
  };
}
